/**
 * BookEarlierWidget — Iter 69.
 *
 * Inverse twin of `StayLongerWidget`. Shifts the renter's entire trip
 * earlier by 1 / 2 / 4 weeks (same duration, different start) and shows
 * how much they would save. Self-hides when no rung shows a positive
 * saving so the panel doesn't clutter the booking flow for off-peak dates.
 *
 * Click a rung → calls `onShift(weeksEarlier)`; parent shifts both start
 * and end dates back by N×7 days and the rest of the booking pipeline
 * re-quotes automatically.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logWidgetEvent } from "@/lib/widgetTracking";
import { Sparkles, CalendarClock } from "lucide-react";
import Money from "@/components/Money";

const WEEK_LABELS = { 1: "1 week earlier", 2: "2 weeks earlier", 4: "4 weeks earlier" };

export default function BookEarlierWidget({ carId, startIso, endIso, onShift }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!carId || !startIso || !endIso) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .post(`/cars/${carId}/earlier-booking-preview`, { start_date: startIso, end_date: endIso })
        .then((r) => {
          if (!cancelled) setPreview(r.data);
        })
        .catch(() => {
          if (!cancelled) setPreview(null);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [carId, startIso, endIso]);

  if (!preview || !preview.options?.length) return null;
  // Filter to rungs with positive savings; hide widget when nothing helps.
  const savingsRows = preview.options.filter((o) => (o.savings || 0) > 0.5);
  if (savingsRows.length === 0) return null;

  const best = savingsRows[0];
  logWidgetEvent("book_earlier", "impression", carId);

  return (
    <div
      className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 shadow-sm"
      data-testid="book-earlier-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center ring-1 ring-emerald-200">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-sm font-semibold text-neutral-900">
            Book earlier, save <Money amount={best.savings} className="text-emerald-700" />
          </div>
          <div className="text-[11px] text-neutral-500 truncate">
            Same {preview.current.days}-day trip · {WEEK_LABELS[best.weeks_earlier]?.toLowerCase() || `${best.weeks_earlier} weeks earlier`}
          </div>
        </div>
      </div>
      <ul className="space-y-1.5" data-testid="book-earlier-options">
        {savingsRows.map((opt) => (
          <li key={opt.weeks_earlier}>
            <button
              type="button"
              onClick={() => { logWidgetEvent("book_earlier", "click", carId, { weeks_earlier: opt.weeks_earlier }); onShift?.(opt.weeks_earlier); }}
              className="w-full flex items-center justify-between gap-3 rounded-xl bg-white/80 hover:bg-white border border-emerald-100 hover:border-emerald-300 hover:shadow-md transition-all px-3 py-2.5 text-left"
              data-testid={`book-earlier-option-${opt.weeks_earlier}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <CalendarClock className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                <span className="text-xs font-semibold text-neutral-800 truncate">
                  {WEEK_LABELS[opt.weeks_earlier]}
                </span>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-emerald-700 font-display">
                  −<Money amount={opt.savings} />
                </div>
                <div className="text-[10px] text-neutral-500">
                  New total <Money amount={opt.total} />
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
